import { useState } from 'react'
import { toast } from 'sonner'
import { Button, Drawer, Input, Select } from '@/components/ui'
import { useCreateTicket } from '../api/queries'
import type { Assignee, Category, Priority } from '../api/types'
import { ASSIGNEE_NAMES, ASSIGNEE_ORDER, CATEGORY_OPTIONS, PRIORITY_OPTIONS } from '../utils/meta'

export interface NewTicketDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (id: string) => void
}

export function NewTicketDrawer({ open, onOpenChange, onCreated }: NewTicketDrawerProps) {
  const create = useCreateTicket()
  const [category, setCategory] = useState<Category>('order_status')
  const [title, setTitle] = useState('')
  const [partnerName, setPartnerName] = useState('')
  const [priority, setPriority] = useState<Priority>('medium')
  const [assignedTo, setAssignedTo] = useState('')
  const [soRef, setSoRef] = useState('')
  const [lot, setLot] = useState('')
  const [notes, setNotes] = useState('')

  const reset = () => {
    setTitle('')
    setPartnerName('')
    setPriority('medium')
    setAssignedTo('')
    setSoRef('')
    setLot('')
    setNotes('')
    setCategory('order_status')
  }

  const submit = () => {
    if (!title.trim()) {
      toast.error('Title is required')
      return
    }
    create.mutate(
      {
        category,
        title: title.trim(),
        priority,
        assigned_to: assignedTo ? (assignedTo as Assignee) : null,
        partner_name: partnerName || null,
        sale_order_refs: soRef
          ? soRef.split(',').map((s) => s.trim()).filter(Boolean)
          : null,
        lot_refs: lot ? lot.split(',').map((s) => s.trim()).filter(Boolean) : null,
        notes: notes || null,
      },
      {
        onSuccess: (card) => {
          toast.success('Ticket created')
          reset()
          onOpenChange(false)
          onCreated(card.id)
        },
        onError: (e) => toast.error(String(e)),
      },
    )
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="standard"
      title="New support ticket"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" loading={create.isPending} onClick={submit}>
            Create ticket
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Category">
          <Select value={category} onChange={(e) => setCategory(e.target.value as Category)}>
            {CATEGORY_OPTIONS.filter((c) => c.value !== 'noise').map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Title">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Where is my order?"
          />
        </Field>
        <Field label="Customer name">
          <Input value={partnerName} onChange={(e) => setPartnerName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Priority">
            <Select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Assigned to">
            <Select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
              <option value="">Auto / Unassigned</option>
              {ASSIGNEE_ORDER.map((a) => (
                <option key={a} value={a}>
                  {ASSIGNEE_NAMES[a]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="SO reference (comma-separated)">
          <Input value={soRef} onChange={(e) => setSoRef(e.target.value)} placeholder="S05423" />
        </Field>
        <Field label="Lot number (comma-separated)">
          <Input value={lot} onChange={(e) => setLot(e.target.value)} />
        </Field>
        <Field label="Notes">
          <textarea
            className="min-h-[64px] w-full rounded-sm border border-border-default bg-surface p-2 text-sm text-primary"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-role-metadata text-tertiary">{label}</span>
      {children}
    </label>
  )
}
